import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import Card from "../../../../components/card";
import { RootState } from "../../../../globals";
import { getAccessPermissions, getErrorMessage } from "../../../../common";
import CatalogService from "../../../../services/catalog.service";
import SimpleDataService from "../../../../services/simpleData.service";
import useEsTelefono from "../../../../utils/esTelefono";
import { veContenidoDelCurso } from "../../portalClase/contenido/panelesDeNivel";
import { BarraFiltros, BuscadorBarra, coincide, FiltroMulti } from "../../../../components/buscadorBarra/buscadorBarra";
import { usePlantel } from "../workshops/comun";
import { cargarCursos, cargarHorariosVivos, olvidarPrecio, precioDeCurso, PrecioCurso } from "./cursosApi";
import { Chip, CORTE_MOVIL, monto, nombreMaestra } from "./piezas";
import CursoVentana from "./cursoVentana";
import ModuloVentana, { PermisosModulo } from "./moduloVentana";

/*
	Cursos con sus Módulos dentro (Horarios Claros, 2026-11-01), en la forma de Matrículas y Promociones: título y «+ Nuevo Curso»,
	pestañas Activos e Inactivos, buscador (curso o módulo) con las pastillas Modalidad y Categoría al lado, tabla (tarjetas en celular)
	y, al dar clic en una fila, la ventana del curso. Reemplaza las pantallas Cursos y Módulos: la ruta /levels abre la misma pantalla
	en la vista Módulos. Las pantallas viejas siguen en el código, sin ruta.
*/

type Vista = "cursos" | "modulos";
type TabEstado = "activos" | "inactivos";

const permisosDe = (p: any): PermisosModulo => ({ read: !!p?.read, create: !!p?.create, update: !!p?.update, delete: !!p?.delete });

const unicos = (xs: { id: number; name: string }[]) => {
	const m = new Map<number, string>();
	xs.forEach((x) => { if (x && x.id != null && !m.has(Number(x.id))) m.set(Number(x.id), x.name); });
	return Array.from(m.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "es"));
};

const CursosController: React.FC = () => {
	const movil = useEsTelefono(CORTE_MOVIL);
	const navigate = useNavigate();
	const ubicacion = useLocation();
	const { campusId } = usePlantel();
	const campusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const campusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const usuario: any = useSelector((state: RootState) => state.userProfile.value);
	const campus: any = campusKey && campusRef ? campusRef[campusKey] : null;
	const simbolo: string = campus?.company?.currency?.symbol || "$";

	const pCursos = permisosDe(getAccessPermissions(usuario, "courses"));
	const pModulos = permisosDe(getAccessPermissions(usuario, "levels"));
	const pHorarios = permisosDe(getAccessPermissions(usuario, "schedules"));
	const puedeContenido = veContenidoDelCurso(getAccessPermissions(usuario, "classPortal"), usuario);

	const [vista, setVista] = useState<Vista>(ubicacion.pathname.indexOf("/levels") === 0 ? "modulos" : "cursos");
	useEffect(() => { setVista(ubicacion.pathname.indexOf("/levels") === 0 ? "modulos" : "cursos"); }, [ubicacion.pathname]);
	const [tab, setTab] = useState<TabEstado>("activos");
	const [q, setQ] = useState<string>("");
	const [modosElegidos, setModosElegidos] = useState<number[]>([]);
	const [categoriasElegidas, setCategoriasElegidas] = useState<number[]>([]);
	const [datos, setDatos] = useState<any[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [vivos, setVivos] = useState<{ [levelId: number]: number } | null>(null);
	const [precios, setPrecios] = useState<{ [courseId: number]: PrecioCurso }>({});
	const [modos, setModos] = useState<any[]>([]);
	const [categorias, setCategorias] = useState<any[]>([]);
	const [cursoAbierto, setCursoAbierto] = useState<number | "nuevo" | null>(null);
	const [moduloAbierto, setModuloAbierto] = useState<{ cursoId: number; nivelId: number | null } | null>(null);

	const recargar = useCallback(async () => {
		if (campusId === null) return;
		setDatos(await cargarCursos(campusId));
	}, [campusId]);

	// Cursos con sus módulos, y los catálogos para las pastillas y el formulario.
	useEffect(() => {
		if (campusId === null) return;
		let vigente = true;
		setDatos(null); setError(null); setVivos(null); setPrecios({}); setCursoAbierto(null); setModuloAbierto(null);
		cargarCursos(campusId).then((d) => { if (vigente) setDatos(d); }).catch((e) => { if (vigente) { setDatos([]); setError(getErrorMessage(e)); } });
		cargarHorariosVivos(campusId).then((v) => { if (vigente) setVivos(v); }).catch(() => { if (vigente) setVivos({}); });
		return () => { vigente = false; };
	}, [campusId]);

	useEffect(() => {
		let vigente = true;
		const token = null;
		CatalogService.getCourseModes(token).then((r: any) => { if (vigente) setModos(r.data || []); }).catch(() => undefined);
		SimpleDataService.getRecords("categories").then((r: any) => { if (vigente) setCategorias(r.data || []); }).catch(() => undefined);
		return () => { vigente = false; };
	}, []);

	// El precio sale del plan de cada curso; se piden de tres en tres, los activos primero, y solo los que faltan (un curso nuevo también entra).
	const pedidos = useRef<{ campus: number | null; ids: Set<number> }>({ campus: null, ids: new Set() });
	useEffect(() => {
		if (campusId === null || !datos) return;
		if (pedidos.current.campus !== campusId) pedidos.current = { campus: campusId, ids: new Set() };
		const ya = pedidos.current.ids;
		const pendientes = datos.filter((c) => !ya.has(Number(c.id))).sort((a, b) => Number(b.status_id) - Number(a.status_id)).map((c) => Number(c.id));
		pendientes.forEach((id) => ya.add(id));
		const trabajar = async () => {
			while (pendientes.length) {
				const id = pendientes.shift() as number;
				const p = await precioDeCurso(campusId, id);
				if (pedidos.current.campus === campusId) setPrecios((prev) => ({ ...prev, [id]: p }));
			}
		};
		void Promise.all([trabajar(), trabajar(), trabajar()]);
	}, [campusId, datos]);

	const recargarPrecio = (courseId: number) => {
		if (campusId === null) return;
		olvidarPrecio(campusId, courseId);
		precioDeCurso(campusId, courseId).then((p) => setPrecios((prev) => ({ ...prev, [courseId]: p })));
	};

	const nivelesDe = (c: any): any[] => c.levels || [];
	const textoNivel = (c: any, n: any) => coincide(q, c.name, c.code, n.name, n.code);
	const pasaFiltros = (c: any) =>
		(!modosElegidos.length || modosElegidos.includes(Number(c.mode_id))) && (!categoriasElegidas.length || categoriasElegidas.includes(Number(c.category_id)));
	const activo = (st: any) => (tab === "activos") === (Number(st) === 1);

	const filasCursos = useMemo(() => (datos || [])
		.filter((c) => activo(c.status_id) && pasaFiltros(c) && (coincide(q, c.name, c.code) || nivelesDe(c).some((n) => textoNivel(c, n))))
		.sort((a, b) => String(a.name).localeCompare(String(b.name), "es")),
	// eslint-disable-next-line react-hooks/exhaustive-deps
	[datos, tab, q, modosElegidos, categoriasElegidas]);

	const filasModulos = useMemo(() => {
		const fs: { curso: any; nivel: any }[] = [];
		(datos || []).forEach((c) => { if (pasaFiltros(c)) nivelesDe(c).forEach((n) => { if (activo(n.status_id) && textoNivel(c, n)) fs.push({ curso: c, nivel: n }); }); });
		return fs.sort((a, b) => String(a.curso.name).localeCompare(String(b.curso.name), "es") || String(a.nivel.name).localeCompare(String(b.nivel.name), "es", { numeric: true }));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [datos, tab, q, modosElegidos, categoriasElegidas]);

	const cuentaActivos = vista === "cursos"
		? (datos || []).filter((c) => Number(c.status_id) === 1).length
		: (datos || []).reduce((s, c) => s + nivelesDe(c).filter((n) => Number(n.status_id) === 1).length, 0);

	const opcionesModo = useMemo(() => modos.length ? unicos(modos.map((m: any) => ({ id: Number(m.id), name: m.name }))) : unicos((datos || []).map((c) => ({ id: Number(c.mode_id), name: c.mode?.name || "-" }))), [datos, modos]);
	const opcionesCategoria = useMemo(() => unicos((datos || []).map((c) => ({ id: Number(c.category_id), name: c.category?.name || "-" }))), [datos]);
	const hayFiltros = q !== "" || modosElegidos.length > 0 || categoriasElegidas.length > 0;
	const limpiar = () => { setQ(""); setModosElegidos([]); setCategoriasElegidas([]); };

	const cursoSel = typeof cursoAbierto === "number" ? (datos || []).find((c) => Number(c.id) === cursoAbierto) || null : null;
	const moduloCurso = moduloAbierto ? (datos || []).find((c) => Number(c.id) === moduloAbierto.cursoId) || null : null;
	const moduloNivel = moduloCurso && moduloAbierto && moduloAbierto.nivelId !== null ? nivelesDe(moduloCurso).find((n) => Number(n.id) === moduloAbierto.nivelId) || null : null;

	const cerrarCurso = () => { if (typeof cursoAbierto === "number") recargarPrecio(cursoAbierto); setCursoAbierto(null); };

	const verHorarios = (c: any) => navigate("/schedules?curso=" + c.id);

	const gris: React.CSSProperties = { fontSize: 13, color: "var(--wa-apagado)" };

	const precioDe = (c: any): React.ReactNode => {
		const p = precios[Number(c.id)];
		if (!p) return <span style={gris}>…</span>;
		if (p.porHorario) return <span>Por Horario</span>;
		if (p.mensualidad === null) return <span className="wcuOro">Sin Plan</span>;
		return <>{monto(simbolo, p.mensualidad)} × {p.meses} meses{p.inscripcion ? <div className="wcuSub">Inscripción {monto(simbolo, p.inscripcion)}</div> : null}</>;
	};
	const vivosDeCurso = (c: any): number | null => vivos ? nivelesDe(c).reduce((s, n) => s + (vivos[n.id] || 0), 0) : null;
	const kitDe = (n: any): React.ReactNode => {
		const ks: any[] = n.kits || [];
		return !ks.length ? <span className="wcuOro">Sin Kit</span> : <>{ks[0].name}{ks.length > 1 ? <span className="wcuSub"> +{ks.length - 1}</span> : null}</>;
	};
	const estado = (st: any) => <Chip tono={Number(st) === 1 ? "acento" : ""}>{Number(st) === 1 ? "Activo" : "Inactivo"}</Chip>;

	const menuCurso = (c: any) => (
		<Dropdown align="end" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
			<Dropdown.Toggle as="button" type="button" className="wcuMas" aria-label="Más"><FontAwesomeIcon icon={solid("ellipsis")} /></Dropdown.Toggle>
			<Dropdown.Menu popperConfig={{ strategy: "fixed" }}>
				{pModulos.create ? <Dropdown.Item as="button" onClick={() => setModuloAbierto({ cursoId: Number(c.id), nivelId: null })}>Nuevo Módulo</Dropdown.Item> : null}
				{pHorarios.read ? <Dropdown.Item as="button" onClick={() => verHorarios(c)}>Ver Horarios</Dropdown.Item> : null}
			</Dropdown.Menu>
		</Dropdown>
	);
	const hayMenu = pModulos.create || pHorarios.read;

	const listaCursos = () => {
		if (!datos) return <div className="loader" />;
		if (!filasCursos.length) return <div style={{ ...gris, textAlign: "center", padding: 24, fontSize: 15 }}>No hay cursos que coincidan.</div>;
		if (movil) return (
			<div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
				{filasCursos.map((c) => (
					<div key={c.id} className="wcuTarjeta" role="button" tabIndex={0} onClick={() => setCursoAbierto(Number(c.id))} onKeyDown={(e) => { if (e.key === "Enter") setCursoAbierto(Number(c.id)); }}>
						<div className="fila"><div><b>{c.name}</b><div className="wcuSub">{[c.code, c.mode?.name].filter(Boolean).join(", ")}</div></div>
							<div style={{ display: "flex", alignItems: "center", gap: 4 }}>{estado(c.status_id)}{hayMenu ? menuCurso(c) : null}</div></div>
						<div style={{ fontSize: 14, marginTop: 4 }}>{precioDe(c)}</div>
						<div className="wcuSub" style={{ marginTop: 2 }}>Módulos {nivelesDe(c).length}, Horarios Vivos {vivosDeCurso(c) ?? "…"}</div>
					</div>
				))}
			</div>
		);
		return (
			<table className="table table-hover wcuLista" style={{ fontSize: 15, marginBottom: 0 }}>
				<thead><tr><th>Curso</th><th className="wcuNum" style={{ width: 90 }}>Módulos</th><th style={{ width: 230 }}>Precio de este Plantel</th><th className="wcuNum" style={{ width: 120 }}>Horarios Vivos</th><th style={{ width: 100 }}>Estado</th>{hayMenu ? <th style={{ width: 40 }} /> : null}</tr></thead>
				<tbody>
					{filasCursos.map((c) => (
						<tr key={c.id} onClick={() => setCursoAbierto(Number(c.id))}>
							<td><b>{c.name}</b><div className="wcuSub">{[c.code, c.mode?.name].filter(Boolean).join(", ")}{c.isComposite ? ", Compuesto" : ""}</div></td>
							<td className="wcuNum">{nivelesDe(c).length}</td>
							<td>{precioDe(c)}</td>
							<td className="wcuNum">{vivosDeCurso(c) ?? "…"}</td>
							<td>{estado(c.status_id)}</td>
							{hayMenu ? <td>{menuCurso(c)}</td> : null}
						</tr>
					))}
				</tbody>
			</table>
		);
	};

	const abrirModulo = (c: any, n: any) => setModuloAbierto({ cursoId: Number(c.id), nivelId: Number(n.id) });

	const listaModulos = () => {
		if (!datos) return <div className="loader" />;
		if (!filasModulos.length) return <div style={{ ...gris, textAlign: "center", padding: 24, fontSize: 15 }}>No hay módulos que coincidan.</div>;
		if (movil) return (
			<div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
				{filasModulos.map(({ curso, nivel }) => (
					<div key={nivel.id} className="wcuTarjeta" role="button" tabIndex={0} onClick={() => abrirModulo(curso, nivel)} onKeyDown={(e) => { if (e.key === "Enter") abrirModulo(curso, nivel); }}>
						<div className="fila"><div><b>{nivel.name}</b><div className="wcuSub">{curso.name}</div></div>{estado(nivel.status_id)}</div>
						<div style={{ fontSize: 14, marginTop: 4 }}>{kitDe(nivel)}</div>
						<div className="wcuSub" style={{ marginTop: 2 }}>Maestras {(nivel.mentors || []).length}, Horarios Vivos {vivos ? (vivos[nivel.id] ?? 0) : "…"}</div>
					</div>
				))}
			</div>
		);
		return (
			<table className="table table-hover wcuLista" style={{ fontSize: 15, marginBottom: 0 }}>
				<thead><tr><th>Módulo</th><th>Kit</th><th>Maestras</th><th className="wcuNum" style={{ width: 120 }}>Horarios Vivos</th><th style={{ width: 100 }}>Estado</th></tr></thead>
				<tbody>
					{filasModulos.map(({ curso, nivel }) => (
						<tr key={nivel.id} onClick={() => abrirModulo(curso, nivel)}>
							<td><b>{nivel.name}</b><div className="wcuSub">{curso.name}, {nivel.code}</div></td>
							<td>{kitDe(nivel)}</td>
							<td style={{ fontSize: 14 }}>{(nivel.mentors || []).length ? (nivel.mentors as any[]).slice(0, 2).map(nombreMaestra).join(", ") + ((nivel.mentors as any[]).length > 2 ? " +" + ((nivel.mentors as any[]).length - 2) : "") : "-"}</td>
							<td className="wcuNum">{vivos ? (vivos[nivel.id] ?? 0) : "…"}</td>
							<td>{estado(nivel.status_id)}</td>
						</tr>
					))}
				</tbody>
			</table>
		);
	};

	if (campusId === null) return <React.Fragment />;

	return (
		<div className="container-fluid waModulo" style={{ textAlign: "left", padding: movil ? "10px 12px 0 12px" : "10px 24px 0 24px" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
				<div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
					<h4 style={{ margin: 0 }}>Cursos</h4>
					<div className="wcuVista" role="group" aria-label="Vista">
						<button type="button" aria-pressed={vista === "cursos"} onClick={() => setVista("cursos")}>Cursos</button>
						<button type="button" aria-pressed={vista === "modulos"} onClick={() => setVista("modulos")}>Módulos</button>
					</div>
				</div>
				{vista === "cursos" && pCursos.create ? <button type="button" className="btn btn-primary waBotonGrande" onClick={() => setCursoAbierto("nuevo")}><FontAwesomeIcon icon={solid("plus")} />&nbsp;Nuevo Curso</button> : null}
			</div>
			{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : null}
			{aviso ? <div className="wcuAviso" role="status">{aviso}<button type="button" className="btn-close" style={{ fontSize: 10 }} aria-label="Cerrar" onClick={() => setAviso(null)} /></div> : null}

			<Card>
				<div style={{ padding: movil ? "12px 12px 12px 12px" : "14px 18px 14px 18px" }}>
					<ul className="nav nav-pills waTabs" style={{ gap: 4 }}>
						{([["activos", "Activos"], ["inactivos", "Inactivos"]] as [TabEstado, string][]).map(([k, t]) => (
							<li className="nav-item" key={k}>
								<button type="button" className={"nav-link btn btn-sm " + (tab === k ? "active" : "")} style={{ padding: "4px 12px", fontSize: 15 }} onClick={() => setTab(k)}>
									{t}{k === "activos" && cuentaActivos ? <span className="waTabCuenta">{cuentaActivos}</span> : null}
								</button>
							</li>
						))}
					</ul>
					<div className="wcuBarra" style={{ marginTop: 12 }}>
						<BarraFiltros hayFiltros={hayFiltros} onLimpiar={limpiar}>
							<BuscadorBarra valor={q} onBuscar={setQ} placeholder="Buscar curso o módulo" />
							{opcionesModo.length > 0 ? <FiltroMulti titulo="Modalidad" opciones={opcionesModo} elegidos={modosElegidos} onChange={setModosElegidos} /> : null}
							{opcionesCategoria.length > 0 ? <FiltroMulti titulo="Categoría" opciones={opcionesCategoria} elegidos={categoriasElegidas} onChange={setCategoriasElegidas} buscar /> : null}
						</BarraFiltros>
					</div>
					<div className="waScrollX" style={{ overflowX: "auto", marginTop: 14 }}>{vista === "cursos" ? listaCursos() : listaModulos()}</div>
				</div>
			</Card>
			<div className="waSobreDock" style={{ marginTop: 10 }} />

			{cursoAbierto !== null && (cursoAbierto === "nuevo" || cursoSel) ? (
				<CursoVentana key={String(cursoAbierto)} campusId={campusId} campus={campus} curso={cursoAbierto === "nuevo" ? null : cursoSel} modos={modos} categorias={categorias}
					vivos={vivos} permisos={pCursos} permisosModulos={pModulos} puedeContenido={puedeContenido}
					onClose={cerrarCurso} onAbrirModulo={(n) => setModuloAbierto({ cursoId: Number(cursoSel?.id), nivelId: n ? Number(n.id) : null })}
					onChanged={async (texto, cerrar) => { await recargar(); setAviso(texto); if (cerrar) cerrarCurso(); }} />
			) : null}
			{moduloAbierto && moduloCurso && (moduloAbierto.nivelId === null || moduloNivel) ? (
				<ModuloVentana key={moduloAbierto.cursoId + "_" + moduloAbierto.nivelId} campusId={campusId} curso={moduloCurso} nivel={moduloNivel} vivos={moduloNivel && vivos ? (vivos[moduloNivel.id] ?? 0) : null}
					permisos={pModulos} puedeNuevoHorario={pHorarios.create} puedeContenido={puedeContenido}
					onClose={() => setModuloAbierto(null)}
					onChanged={async (texto, cerrar) => { await recargar(); setAviso(texto); if (cerrar) setModuloAbierto(null); }} />
			) : null}
		</div>
	);
};

export default CursosController;
